// Detalle de excepción en overlay responsive (Modal desktop / Drawer móvil).
// Muestra todos los datos del CobroExcepcionResponse SIN navegar; reemplaza a
// ExcepcionDetailPage en el flujo de listado (fuente de verdad de campos y
// acciones).
import { useState } from 'react';
import { Button, Divider, Space, Typography, message, theme } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { DetalleOverlay } from '../../../components/DetalleOverlay';
import { CampoInfo, CamposInfoGrid, MontoDestacado } from '../../../components/CampoInfo';
import { EmptyState } from '../../../components/EmptyState';
import { DeleteExcepcionConfirmModal } from './DeleteExcepcionConfirmModal';
import { ExcepcionEditModal } from './ExcepcionEditModal';
import { useExcepcion, useDeleteExcepcion } from '../api/useExcepciones';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { DATE_FORMAT } from '../../../lib/date';
import dayjs from 'dayjs';
import { formatCurrency, Permission } from '@aid/shared';
import { excepcionAmount } from '../lib/excepcionAmount';

const { Text } = Typography;

interface ExcepcionDetailModalProps {
  id: number | null;
  onClose: () => void;
}

export function ExcepcionDetailModal({ id, onClose }: ExcepcionDetailModalProps) {
  const t = useT();
  const { data: excepcion, isLoading, error, refetch } = useExcepcion(id ?? 0);
  const deleteExcepcion = useDeleteExcepcion();
  const canUpdate = useHasPermission(Permission.EXCEPCIONES_UPDATE);
  const canDelete = useHasPermission(Permission.EXCEPCIONES_DELETE);
  const { token } = theme.useToken();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const handleDelete = async () => {
    if (!excepcion) return;
    try {
      await deleteExcepcion.mutateAsync(excepcion.id);
      message.success(t('pages.excepciones.deleteSuccess'));
      onClose();
    } catch {
      message.error(t('pages.excepciones.deleteError'));
    }
  };

  const footer = (
    <Space wrap>
      {canDelete && excepcion && (
        <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteModalOpen(true)}>
          {t('common.delete')}
        </Button>
      )}
      {canUpdate && excepcion && (
        <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
          {t('common.edit')}
        </Button>
      )}
      <Button onClick={onClose}>{t('common.close')}</Button>
    </Space>
  );

  const amount = excepcion ? excepcionAmount(excepcion) : null;

  return (
    <>
      <DetalleOverlay
        open={id !== null}
        onClose={onClose}
        title={id !== null ? t('pages.excepciones.detailId', { id }) : undefined}
        footer={footer}
        loading={isLoading}
      >
        {error || !excepcion ? (
          <EmptyState
            title={t('pages.excepciones.notFound')}
            description={t('pages.excepciones.notFoundDetail')}
          />
        ) : (
          <div>
            <CamposInfoGrid>
              <CampoInfo label={t('common.id')} value={excepcion.id} />
              <CampoInfo
                label={t('pages.excepciones.cliente')}
                value={
                  excepcion.cliente
                    ? `${excepcion.cliente.nombre ?? ''} (#${excepcion.cliente.id})`
                    : (excepcion.idCliente ?? '-')
                }
              />
              <CampoInfo full label={t('pages.excepciones.motivo')} value={excepcion.motivo} />
              <CampoInfo
                label={t('pages.excepciones.valor')}
                value={
                  amount ? (
                    <MontoDestacado
                      value={formatCurrency(amount)}
                      color={
                        Number(amount) < 0
                          ? token.colorError
                          : Number(amount) > 0
                            ? token.colorSuccess
                            : undefined
                      }
                    />
                  ) : (
                    '-'
                  )
                }
              />
              <CampoInfo
                label={t('pages.excepciones.usuario')}
                value={
                  excepcion.usuario
                    ? (excepcion.usuario.username ?? excepcion.usuario.email)
                    : excepcion.idUsuario
                }
              />
            </CamposInfoGrid>

            <Divider style={{ margin: '22px 0 18px', borderColor: token.colorSplit }} />
            <Text
              type="secondary"
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.4,
                marginBottom: 14,
              }}
            >
              {t('pages.clientes.infoAuditoria')}
            </Text>
            <CamposInfoGrid>
              <CampoInfo
                label={t('common.createdAt')}
                value={
                  excepcion.fechaCreacion ? dayjs(excepcion.fechaCreacion).format(DATE_FORMAT) : '-'
                }
              />
              <CampoInfo
                label={t('common.updatedAt')}
                value={
                  excepcion.fechaModificacion
                    ? dayjs(excepcion.fechaModificacion).format(DATE_FORMAT)
                    : '-'
                }
              />
            </CamposInfoGrid>
          </div>
        )}
      </DetalleOverlay>
      <DeleteExcepcionConfirmModal
        open={deleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        loading={deleteExcepcion.isPending}
      />
      {editOpen && excepcion && (
        <ExcepcionEditModal
          id={excepcion.id}
          onClose={() => setEditOpen(false)}
          onSuccess={refetch}
        />
      )}
    </>
  );
}
